/**
 * Page forms (owner decision, 16 October 2026): a funnel shown as a classic form on a page, for
 * example the clinic's contact page. The questions are the funnel's own; questions that depend on
 * earlier answers appear as those answers are given. Consent is part of the form and must be
 * ticked before anything is sent (D4). Nothing is stored until the form is sent.
 *
 * Runs at /form/<slug|clinicId>/<formId>, embedded by the loader (`data-wapadesk-inline="form:<id>"`)
 * or opened as a link. File questions belong to the chat and are left out here.
 */
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { t, type Locale } from "@wapadesk/i18n";
import {
  renderTemplate,
  validateAnswer,
  visibleNodes,
  type Answers,
  type FlatNode,
} from "@wapadesk/engine";
import type { MessageTemplate, WidgetConfigResponse } from "@wapadesk/schema";
import { DEFAULT_TEMPLATE, renderWhatsAppMessage } from "@wapadesk/message";
import { FormApi } from "../lib/form-api";
import { ApiClientError } from "../lib/api";
import { notLicensed } from "../lib/embed";
import { Tracker } from "../lib/track";
import { COUNTRIES } from "./inputs";
import { WaIcon } from "./WaIcon";

type Config = WidgetConfigResponse;

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Loads Cloudflare Turnstile once (explicit rendering). */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile"));
    s.onerror = () => reject(new Error("turnstile"));
    document.head.appendChild(s);
  });
}
const SKIP = new Set(["file_upload", "booking", "payment"]);

/**
 * Chat wording on a page (16 October 2026): sentences that greet the person by an earlier answer
 * ("Thanks {{answers.first_name | firstName}}.") read oddly when every question shows at once, so
 * they are left out; the rest is rendered as usual.
 */
export function pageText(text: string): string {
  const kept = text.split(/(?<=[.!?])\s+/).filter((sentence) => !sentence.includes("{{answers"));
  return kept.join(" ").trim();
}

/** Height hints for the loader, so the embed grows with the form instead of scrolling inside. */
function postHeight(): void {
  if (window.parent === window || !document.referrer) return;
  try {
    // The app's body is sized to the frame, so measure the form itself.
    const main = document.querySelector<HTMLElement>("main.pf");
    // Include the form's own offset (body margin), so the frame never needs an inner scrollbar.
    const height = Math.ceil(
      main ? main.offsetTop + main.scrollHeight + 2 : document.documentElement.scrollHeight,
    );
    window.parent.postMessage(
      { source: "wapadesk", type: "resize", payload: { height } },
      new URL(document.referrer).origin,
    );
  } catch {
    /* no parent to tell */
  }
}

function toParent(message: unknown): void {
  if (window.parent === window || !document.referrer) return;
  try {
    window.parent.postMessage(message, new URL(document.referrer).origin);
  } catch {
    /* no parent */
  }
}

function openNewTab(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    if (document.hasFocus())
      toParent({ source: "wapadesk", type: "navigate", payload: { url, target: "_blank" } });
  }, 400);
}

export function FormPage({
  clinicRef,
  formId,
  apiOrigin,
  preview,
}: {
  clinicRef: string;
  formId: string;
  apiOrigin: string;
  /** Dashboard live preview (16 October 2026): the draft, nothing sent, nothing stored. */
  preview?: { config: Config; template?: Partial<MessageTemplate> } | undefined;
}) {
  const api = useMemo(() => new FormApi(apiOrigin), [apiOrigin]);
  // The page the form sits on: the loader passes its address without the query string (the
  // iframe's referrer is only the site's origin); opened as a link, it is this page.
  const passed = new URLSearchParams(location.search).get("page");
  const pageUrl =
    passed && /^https?:\/\//.test(passed)
      ? passed
      : document.referrer && window.parent !== window
        ? document.referrer
        : location.href;
  const [cfg, setCfg] = useState<Config | undefined>();
  const [failed, setFailed] = useState<false | "error" | "not_licensed">(false);
  const [answers, setAnswers] = useState<Answers>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ ref: string } | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [waBusy, setWaBusy] = useState(false);
  const [previewMessage, setPreviewMessage] = useState<string | undefined>();
  // Cloudflare Turnstile (16 October 2026): rendered only when the deployment checks it. Usually
  // invisible; it asks for a click only when Cloudflare is unsure. Tokens are single-use.
  const challengeEl = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>();
  const token = useRef<string | undefined>();
  const tokenWaiters = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (preview) {
      setCfg(preview.config);
      return;
    }
    let cancelled = false;
    api
      .config(clinicRef, formId, pageUrl)
      .then((c) => {
        if (cancelled) return;
        if (c.mode !== "active" || !c.form || c.form.formId !== formId) setFailed("error");
        else setCfg(c);
      })
      .catch((e: unknown) => !cancelled && setFailed(notLicensed(e) ? "not_licensed" : "error"));
    return () => {
      cancelled = true;
    };
  }, [api, clinicRef, formId, pageUrl, preview]);

  useEffect(() => {
    postHeight();
    const ro = new ResizeObserver(() => postHeight());
    ro.observe(document.body);
    const main = document.querySelector("main.pf");
    if (main) ro.observe(main);
    return () => ro.disconnect();
  }, [cfg, sent]);

  useEffect(() => {
    const ch = cfg?.challenge;
    if (!ch || sent || !challengeEl.current || widgetId.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !challengeEl.current) return;
        widgetId.current = ts.render(challengeEl.current, {
          sitekey: ch.sitekey,
          action: ch.action,
          appearance: "interaction-only",
          language: (cfg.clinic.defaultLocale ?? "en-GB").slice(0, 2),
          callback: (t: string) => {
            token.current = t;
            tokenWaiters.current.splice(0).forEach((w) => w());
          },
          "expired-callback": () => (token.current = undefined),
          "error-callback": () => (token.current = undefined),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cfg, sent]);

  /** The token, waiting a few seconds for Turnstile to finish in the background. */
  const challengeToken = (): Promise<string | undefined> =>
    token.current || !cfg?.challenge
      ? Promise.resolve(token.current)
      : new Promise((resolve) => {
          const timer = setTimeout(() => resolve(token.current), 8000);
          tokenWaiters.current.push(() => {
            clearTimeout(timer);
            resolve(token.current);
          });
        });

  const tracker = useMemo(() => {
    if (!cfg?.form || preview) return undefined;
    const width = window.innerWidth;
    let path: string | undefined;
    try {
      path = new URL(pageUrl).pathname;
    } catch {
      /* no path */
    }
    return new Tracker(api, {
      clinicId: cfg.clinicId,
      ...(path ? { pagePath: path } : {}),
      device: width < 640 ? "mobile" : width < 1024 ? "tablet" : "desktop",
      locale: cfg.clinic.defaultLocale,
      formId: cfg.form.formId,
      formVersionId: cfg.form.formVersionId,
    });
  }, [api, cfg, pageUrl, preview]);

  if (failed)
    return (
      <main class="bp">
        <div class="bp-body">
          <p class="bp-hint">
            {t("en-GB", failed === "not_licensed" ? "chrome.notOnThisSite" : "chrome.errorBody")}
          </p>
        </div>
      </main>
    );
  if (!cfg?.form)
    return (
      <main class="bp center" aria-busy="true">
        <div class="spinner" aria-label="Loading" />
      </main>
    );

  const locale = (cfg.clinic.defaultLocale ?? "en-GB") as Locale;
  const form = cfg.form.schema;
  const ctx = { clinic: { name: cfg.clinic.name }, answers };
  // Consent comes first in every funnel (it must, before any question); on a page it reads
  // naturally just above the send button, so it is shown last.
  const all = visibleNodes(form, answers).filter((f) => !SKIP.has(f.node.type));
  // Closing chat messages after the last question ("Send this to us on WhatsApp…") are left out.
  const lastInput = all.reduce((i, f, k) => (f.node.type !== "message" ? k : i), -1);
  const visible = all.filter(
    (f, k) =>
      (f.node.type !== "message" || k < lastInput) &&
      (f.node.type !== "message" || pageText(f.node.text) !== ""),
  );
  const shown = [
    ...visible.filter((f) => f.node.type !== "consent"),
    ...visible.filter((f) => f.node.type === "consent"),
  ];
  const settings = cfg.form.pageForm;
  const set = (id: string, v: unknown) => {
    setAnswers((a) => ({ ...a, [id]: v as Answers[string] }));
    setErrors((e) => {
      const { [id]: _drop, ...rest } = e;
      return rest;
    });
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    const out: Record<string, unknown> = {};
    for (const f of shown) {
      if (f.node.type === "message") continue;
      const raw = answers[f.node.id];
      const v = validateAnswer(f.node, raw ?? (f.node.type === "consent" ? {} : ""));
      if (!v.ok) found[f.node.id] = v.code;
      else if (v.value !== "" && !(Array.isArray(v.value) && v.value.length === 0))
        out[f.node.id] = v.value;
    }
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`q-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    if (preview) {
      // Preview: show the thank-you panel; nothing is sent.
      setPreviewMessage(undefined);
      setSent({ ref: "PREVIEW" });
      return;
    }
    setBusy(true);
    setProblem(undefined);
    const challenge = await challengeToken();
    try {
      const r = await api.submit({
        ...(challenge ? { challengeToken: challenge } : {}),
        clinicId: cfg.clinicId,
        formId: cfg.form!.formId,
        formVersionId: cfg.form!.formVersionId,
        answers: out,
        locale,
        context: {
          pageUrl,
          ...(navigator.language ? { language: navigator.language.slice(0, 16) } : {}),
        },
      });
      tracker?.track("form_completed", { trigger: "page_form" });
      tracker?.flush();
      toParent({ source: "wapadesk", type: "track", payload: { event: "lead_completed" } });
      setSent({ ref: r.ref });
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        err.code === "invalid_answers" &&
        Array.isArray(err.details)
      ) {
        const map: Record<string, string> = {};
        for (const d of err.details as Array<{ nodeId: string; code: string }>)
          map[d.nodeId] = d.code;
        setErrors(map);
      } else if (err instanceof ApiClientError && err.code === "challenge_failed")
        setProblem(t(locale, "pf.challengeFailed"));
      else setProblem(t(locale, "bp.handoffFailed"));
    } finally {
      // A token is redeemed once; get a fresh one for another attempt.
      token.current = undefined;
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
      setBusy(false);
    }
  };

  const onWhatsApp = async () => {
    if (preview && cfg?.form) {
      setPreviewMessage(
        renderWhatsAppMessage({
          form: cfg.form.schema,
          answers,
          template: { ...DEFAULT_TEMPLATE, ...(preview.template ?? {}) },
          clinic: { name: cfg.clinic.name },
          lead: { ref: "PREVIEW", fileCount: 0 },
          pageUrl: "https://your-website.example/contact",
          optionLabels: Object.fromEntries(cfg.procedures.map((p) => [p.id, p.name])),
        }),
      );
      return;
    }
    if (waBusy) return;
    setWaBusy(true);
    try {
      const h = await api.handoff({ type: "whatsapp", atNodeId: "form" });
      tracker?.track("whatsapp_click", { atNodeId: "form", early: false });
      tracker?.flush();
      toParent({ source: "wapadesk", type: "track", payload: { event: "whatsapp_open" } });
      openNewTab(h.url);
    } catch {
      setProblem(t(locale, "bp.handoffFailed"));
    } finally {
      setWaBusy(false);
    }
  };

  const errorText = (code: string | undefined) =>
    code
      ? (t(locale, `validation.${code}` as "validation.required") ??
        t(locale, "validation.required"))
      : undefined;

  return (
    <main
      class={`bp pf${new URLSearchParams(location.search).get("embed") === "1" ? " embedded" : ""}`}
      style={cfg.branding.color ? `--bp-accent:${cfg.branding.color}` : undefined}
    >
      <div class="bp-body">
        {sent ? (
          <div class="bp-stack">
            <div class="bp-thanks" role="status">
              <svg class="bp-tick" viewBox="0 0 52 52" width="52" height="52" aria-hidden="true">
                <circle cx="26" cy="26" r="24" />
                <path d="M15 27 l7 7 l15 -16" />
              </svg>
              <div>
                <strong>{t(locale, "pf.sentTitle")}</strong>
                <p>{settings?.thankYou || t(locale, "pf.sentBody")}</p>
              </div>
            </div>
            {settings?.showWhatsApp !== false && cfg.whatsappNumber && (
              <>
                <p class="bp-hint">{t(locale, "pf.whatsAppHint")}</p>
                <button
                  type="button"
                  class="bp-wa"
                  disabled={waBusy}
                  onClick={() => void onWhatsApp()}
                >
                  <WaIcon /> {t(locale, "chrome.openWhatsApp")}
                </button>
                {previewMessage !== undefined && (
                  <pre class="pf-preview-message">{previewMessage}</pre>
                )}
              </>
            )}
            {problem && (
              <p class="bp-error" role="alert">
                {problem}
              </p>
            )}
          </div>
        ) : (
          <form class="bp-stack" noValidate onSubmit={(e) => void submit(e)}>
            {shown.map((f) => (
              <Question
                key={f.node.id}
                f={f}
                locale={locale}
                prompt={renderTemplate(
                  pageText(f.node.type === "message" ? f.node.text : (f.node.prompt ?? "")) ||
                    (f.node.type === "message" ? "" : (f.node.message?.label ?? f.node.id)),
                  ctx,
                )}
                clinicName={cfg.clinic.name}
                procedures={cfg.procedures}
                value={answers[f.node.id]}
                error={errorText(errors[f.node.id])}
                onChange={(v) => set(f.node.id, v)}
              />
            ))}
            {cfg.challenge && <div ref={challengeEl} class="pf-challenge" />}
            {problem && (
              <p class="bp-error" role="alert">
                {problem}
                {cfg.whatsappNumber && (
                  <>
                    {" "}
                    <a
                      href={`https://wa.me/${cfg.whatsappNumber.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener"
                    >
                      {t(locale, "chrome.continueOnWhatsApp")}
                    </a>
                  </>
                )}
              </p>
            )}
            <button type="submit" class="bp-primary" disabled={busy} aria-busy={busy}>
              {busy
                ? t(locale, "chrome.sendingEmail")
                : settings?.submitLabel || t(locale, "pf.send")}
            </button>
          </form>
        )}
      </div>
      {cfg.branding.showBadge !== false && (
        <footer class="bp-foot">
          <a
            href={`https://wapadesk.com/?utm_source=widget&utm_medium=badge&utm_campaign=page_form&ref=${encodeURIComponent(cfg.clinic.slug)}`}
            target="_blank"
            rel="noopener"
          >
            Powered by Wapadesk
          </a>
        </footer>
      )}
    </main>
  );
}

function Question({
  f,
  locale,
  prompt,
  clinicName,
  procedures,
  value,
  error,
  onChange,
}: {
  f: FlatNode;
  locale: Locale;
  prompt: string;
  clinicName: string;
  procedures: Config["procedures"];
  value: unknown;
  error: string | undefined;
  onChange: (v: unknown) => void;
}) {
  const n = f.node;
  const id = `q-${n.id}`;
  const help = n.help ? <span class="bp-hint">{n.help}</span> : null;
  const err = error ? (
    <span class="bp-error" role="alert">
      {error}
    </span>
  ) : null;
  const req = n.required ? <span aria-hidden="true"> *</span> : null;

  if (n.type === "message") return <p class="pf-message">{prompt}</p>;

  if (n.type === "consent") {
    const v = (value as Record<string, boolean> | undefined) ?? {};
    return (
      <fieldset class="bp-field" id={id} tabIndex={-1}>
        {n.items.map((item) => (
          <label key={item.key} class="bp-check">
            <input
              type="checkbox"
              checked={v[item.key] === true}
              onChange={(e) =>
                onChange({ ...v, [item.key]: (e.target as HTMLInputElement).checked })
              }
            />
            <span>
              {renderTemplate(item.text, { clinic: { name: clinicName }, answers: {} })}
              {item.required ? " *" : ""}
            </span>
          </label>
        ))}
        {n.policyUrl && (
          <a class="bp-hint" href={n.policyUrl} target="_blank" rel="noopener">
            {t(locale, "bp.privacy")}
          </a>
        )}
        {err}
      </fieldset>
    );
  }

  if (n.type === "single_choice" || n.type === "multi_choice" || n.type === "procedure_picker") {
    const multi = n.type === "multi_choice" || (n.type === "procedure_picker" && n.multiple);
    const options =
      n.type === "procedure_picker"
        ? procedures.map((p) => ({ id: p.id, label: p.name, group: p.category }))
        : n.options.map((o) => ({ id: o.id, label: o.label, group: "" }));
    const picked = Array.isArray(value)
      ? (value as string[])
      : typeof value === "string"
        ? [value]
        : [];
    const toggle = (o: string) =>
      multi
        ? onChange(picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o])
        : onChange(n.type === "procedure_picker" ? [o] : o);
    const groups = [...new Set(options.map((o) => o.group))];
    return (
      <fieldset class="bp-field" id={id} tabIndex={-1}>
        <legend>
          {prompt}
          {req}
        </legend>
        {help}
        {groups.map((g) => (
          <div key={g} class="bp-group">
            {g && <div class="bp-cat">{g}</div>}
            <div class="bp-chips" role={multi ? "group" : "radiogroup"}>
              {options
                .filter((o) => o.group === g)
                .map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role={multi ? "checkbox" : "radio"}
                    aria-checked={picked.includes(o.id)}
                    class={`bp-chip ${picked.includes(o.id) ? "on" : ""}`}
                    onClick={() => toggle(o.id)}
                  >
                    {o.label}
                  </button>
                ))}
            </div>
          </div>
        ))}
        {err}
      </fieldset>
    );
  }

  if (n.type === "phone")
    return (
      <PhoneField
        id={id}
        n={n}
        prompt={prompt}
        req={req}
        help={help}
        err={err}
        value={value}
        onChange={onChange}
      />
    );

  const long = n.type === "long_text";
  const type =
    n.type === "email"
      ? "email"
      : n.type === "date"
        ? "date"
        : n.type === "number"
          ? "number"
          : "text";
  const auto =
    n.maps === "contact.email"
      ? "email"
      : n.maps === "contact.firstName"
        ? "given-name"
        : n.maps === "contact.lastName"
          ? "family-name"
          : n.maps === "contact.fullName"
            ? "name"
            : undefined;
  return (
    <label class="bp-field">
      <span class="bp-label">
        {prompt}
        {req}
      </span>
      {help}
      {long ? (
        <textarea
          id={id}
          rows={4}
          maxLength={"maxLength" in n && n.maxLength ? n.maxLength : 800}
          value={typeof value === "string" ? value : ""}
          aria-invalid={Boolean(err)}
          onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
          autoComplete={auto}
          maxLength={
            type === "text" ? ("maxLength" in n && n.maxLength ? n.maxLength : 300) : undefined
          }
          aria-invalid={Boolean(err)}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
      )}
      {err}
    </label>
  );
}

function PhoneField({
  id,
  n,
  prompt,
  req,
  help,
  err,
  value,
  onChange,
}: {
  id: string;
  n: FlatNode["node"] & { defaultCountry?: string };
  prompt: string;
  req: preact.ComponentChildren;
  help: preact.ComponentChildren;
  err: preact.ComponentChildren;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const [country, setCountry] = useState(n.defaultCountry ?? "GB");
  const [local, setLocal] = useState(typeof value === "string" ? value : "");
  const dial = COUNTRIES.find((c) => c[0] === country)?.[1] ?? "+44";
  const toE164 = (raw: string, d: string) => {
    const v = raw.replace(/[\s().-]/g, "");
    if (!v) return "";
    if (v.startsWith("+")) return v;
    if (v.startsWith("00")) return `+${v.slice(2)}`;
    return `${d}${v.replace(/^0/, "")}`;
  };
  return (
    <div class="bp-field">
      <label class="bp-label" for={id}>
        {prompt}
        {req}
      </label>
      {help}
      <div class="pf-phone">
        <select
          aria-label="Country code"
          value={country}
          onChange={(e) => {
            const c = (e.target as HTMLSelectElement).value;
            setCountry(c);
            onChange(toE164(local, COUNTRIES.find((x) => x[0] === c)?.[1] ?? "+44"));
          }}
        >
          {COUNTRIES.map(([code, d, name]) => (
            <option key={code} value={code}>
              {name} {d}
            </option>
          ))}
        </select>
        <input
          id={id}
          type="tel"
          autoComplete="tel"
          value={local}
          aria-invalid={Boolean(err)}
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setLocal(v);
            onChange(toE164(v, dial));
          }}
        />
      </div>
      {err}
    </div>
  );
}
